import React, { useState } from "react"
import { Search, Plus, Menu, Settings, User } from "lucide-react"
import { Input } from "@aix/ui"

interface HeaderProps {
  onToggleSidebar: () => void
  onToggleRightPanel: () => void
  sidebarCollapsed: boolean
  rightPanelCollapsed: boolean
}

export const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  onToggleRightPanel,
  sidebarCollapsed,
  rightPanelCollapsed,
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [showNewChatMenu, setShowNewChatMenu] = useState(false)

  return (
    <div className="h-16 px-4 flex items-center justify-between bg-background border-b border-border">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        <button
          onClick={onToggleSidebar}
          className="p-2 hover:bg-muted rounded-md transition-colors"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3">
          <div className="text-xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            🤖 AI Chat Hub
          </div>
        </div>
      </div>

      {/* Center Section - Search */}
      <div className="flex-1 max-w-md mx-8 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search conversations, projects, or messages..."
          className="pl-10 w-full border-border"
        />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* New Chat Button */}
        <div className="relative">
          <button
            onClick={() => setShowNewChatMenu(!showNewChatMenu)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            New Chat
          </button>

          {showNewChatMenu && (
            <div className="absolute top-full mt-2 right-0 w-64 bg-background border border-border rounded-md shadow-lg z-50">
              <div className="p-2">
                <div className="text-xs font-medium text-muted-foreground mb-2">Quick Start</div>
                <button className="w-full text-left px-3 py-2 hover:bg-muted rounded text-sm">
                  📋 Task Assistant Chat
                </button>
                <button className="w-full text-left px-3 py-2 hover:bg-muted rounded text-sm">
                  💬 General Conversation
                </button>
                <button className="w-full text-left px-3 py-2 hover:bg-muted rounded text-sm">
                  🔧 Debug & Test Tools
                </button>
                <div className="border-t border-border my-2"></div>
                <button className="w-full text-left px-3 py-2 hover:bg-muted rounded text-sm text-primary">
                  + Custom Conversation
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Settings */}
        <button className="p-2 hover:bg-muted rounded-md transition-colors" title="Settings">
          <Settings className="h-5 w-5" />
        </button>

        {/* Right Panel Toggle */}
        <button
          onClick={onToggleRightPanel}
          className="p-2 hover:bg-muted rounded-md transition-colors"
          title={rightPanelCollapsed ? "Show context panel" : "Hide context panel"}
        >
          <div className="flex gap-1">
            <div className={`w-1 h-5 bg-current ${rightPanelCollapsed ? "opacity-50" : ""}`}></div>
            <div className={`w-1 h-5 bg-current ${rightPanelCollapsed ? "opacity-50" : ""}`}></div>
            <div className={`w-1 h-5 bg-current ${rightPanelCollapsed ? "opacity-50" : ""}`}></div>
          </div>
        </button>

        {/* User Profile */}
        <button className="p-2 hover:bg-muted rounded-md transition-colors" title="Profile">
          <User className="h-5 w-5" />
        </button>
      </div>

      {/* Click outside to close menu */}
      {showNewChatMenu && <div className="fixed inset-0 z-40" onClick={() => setShowNewChatMenu(false)} />}
    </div>
  )
}
